import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ProtectedRoute } from './ProtectedRoute';

// Lazy-loaded page components
const BrandingPage = React.lazy(() => import('../../features/branding/pages/BrandingPage').then(m => ({ default: m.BrandingPage })));
const PricingPage = React.lazy(() => import('../../features/branding/pages/PricingPage').then(m => ({ default: m.PricingPage })));
const AboutPage = React.lazy(() => import('../../features/branding/pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ContactPage = React.lazy(() => import('../../features/branding/pages/ContactPage').then(m => ({ default: m.ContactPage })));
const LoginPage = React.lazy(() => import('../../features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('../../features/auth/pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const EmailVerificationPage = React.lazy(() => import('../../features/auth/pages/EmailVerificationPage').then(m => ({ default: m.EmailVerificationPage })));
const ForgotPasswordPage = React.lazy(() => import('../../features/auth/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = React.lazy(() => import('../../features/auth/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const DashboardPage = React.lazy(() => import('../../features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const StaffPage = React.lazy(() => import('../../features/users/pages/StaffPage').then(m => ({ default: m.StaffPage })));
const CustomersPage = React.lazy(() => import('../../features/customers/pages/CustomersPage').then(m => ({ default: m.CustomersPage })));
const JobsPage = React.lazy(() => import('../../features/jobs/pages/JobsPage').then(m => ({ default: m.JobsPage })));
const JobDetailPage = React.lazy(() => import('../../features/jobs/pages/JobDetailPage').then(m => ({ default: m.JobDetailPage })));
const CreateJobPage = React.lazy(() => import('../../features/jobs/pages/CreateJobPage').then(m => ({ default: m.CreateJobPage })));
const TasksPage = React.lazy(() => import('../../features/tasks/pages/TasksPage').then(m => ({ default: m.TasksPage })));
const TaskDetailPage = React.lazy(() => import('../../features/tasks/pages/TaskDetailPage').then(m => ({ default: m.TaskDetailPage })));
const TeamsPage = React.lazy(() => import('../../features/teams/pages/TeamsPage').then(m => ({ default: m.TeamsPage })));
const TeamViewPage = React.lazy(() => import('../../features/teams/pages/TeamViewPage').then(m => ({ default: m.TeamViewPage })));
const ProfilePage = React.lazy(() => import('../../features/auth/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const CompanySettingsPage = React.lazy(() => import('../../features/companies/pages/CompanySettingsPage').then(m => ({ default: m.CompanySettingsPage })));

const PageLoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

export function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path={ROUTES.BRANDING} element={<BrandingPage />} />
        <Route path={ROUTES.PRICING} element={<PricingPage />} />
        <Route path={ROUTES.ABOUT} element={<AboutPage />} />
        <Route path={ROUTES.CONTACT} element={<ContactPage />} />
        <Route
          path={ROUTES.LOGIN}
          element={isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <LoginPage />}
        />
        <Route
          path={ROUTES.REGISTER}
          element={isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <RegisterPage />}
        />
        <Route path={ROUTES.VERIFY_EMAIL} element={<EmailVerificationPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />

        {/* Protected admin routes */}
        <Route path="/admin" element={<ProtectedRoute><Navigate to={ROUTES.DASHBOARD} replace /></ProtectedRoute>} />

        <Route path={ROUTES.DASHBOARD} element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />


        <Route path={ROUTES.STAFF} element={<ProtectedRoute requiredPermission="canViewUsers"><StaffPage /></ProtectedRoute>} />

        <Route path={ROUTES.CUSTOMERS} element={<ProtectedRoute requiredPermission="canViewCustomers"><CustomersPage /></ProtectedRoute>} />

        <Route path={ROUTES.JOBS} element={<ProtectedRoute requiredPermission="canViewJobs"><JobsPage /></ProtectedRoute>} />
        <Route path={ROUTES.JOB_CREATE} element={<ProtectedRoute requiredPermission="canManageJobs"><CreateJobPage /></ProtectedRoute>} />
        <Route path={ROUTES.JOB_DETAIL} element={<ProtectedRoute requiredPermission="canViewJobs"><JobDetailPage /></ProtectedRoute>} />

        <Route path={ROUTES.TASKS} element={<ProtectedRoute requiredPermission="canViewTasks"><TasksPage /></ProtectedRoute>} />
        <Route path={ROUTES.TASK_DETAIL} element={<ProtectedRoute requiredPermission="canViewTasks"><TaskDetailPage /></ProtectedRoute>} />

        <Route path={ROUTES.TEAMS} element={<ProtectedRoute requiredPermission="canViewTeams"><TeamsPage /></ProtectedRoute>} />
        <Route path={ROUTES.TEAM_VIEW} element={<ProtectedRoute requiredPermission="canViewTeams"><TeamViewPage /></ProtectedRoute>} />

        <Route path={ROUTES.PROFILE} element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path={ROUTES.COMPANY_SETTINGS} element={<ProtectedRoute requiredPermission="canViewCompanies"><CompanySettingsPage /></ProtectedRoute>} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.BRANDING} replace />} />
        <Route path="*" element={<Navigate to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.BRANDING} replace />} />
      </Routes>
    </Suspense>
  );
}
